import React, { useState, useEffect } from "react";
import { vehicleService } from "../services/api";
import "./ComparisonPage.css";

const NA = "Chưa có thông tin";
const val = (v) => (v !== null && v !== undefined && v !== "") ? v : NA;

const ComparisonPage = () => {
  const [variants, setVariants] = useState([]);
  const [variant1Id, setVariant1Id] = useState("");
  const [variant2Id, setVariant2Id] = useState("");
  const [vehicle1, setVehicle1] = useState(null);
  const [vehicle2, setVehicle2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchVariants = async () => {
      try {
        const data = await vehicleService.getAllVariants();
        setVariants(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching variants:", err);
      }
    };
    fetchVariants();
  }, []);

  const handleCompare = async () => {
    if (!variant1Id || !variant2Id) {
      setError("Vui lòng chọn 2 phiên bản xe để so sánh");
      return;
    }
    if (variant1Id === variant2Id) {
      setError("Vui lòng chọn 2 phiên bản khác nhau");
      return;
    }
    setError("");
    try {
      setLoading(true);
      const data = await vehicleService.compareVehicles(variant1Id, variant2Id);
      setVehicle1(data.vehicle1);
      setVehicle2(data.vehicle2);
    } catch (err) {
      setError("Lỗi khi tải thông tin so sánh. Vui lòng thử lại.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return NA;
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const BetterTick = () => (
    <svg className="better-tick" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 13L9 17L19 7" stroke="#00FF66" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="tick-path" style={{ filter: "drop-shadow(0 0 8px rgba(0, 255, 102, 0.8))" }} />
      {/* Sparkles */}
      <circle cx="19" cy="4" r="1" fill="#fff" className="sparkle s1" />
      <circle cx="21" cy="9" r="1.5" fill="#00FF66" className="sparkle s2" />
      <circle cx="4" cy="8" r="1" fill="#fff" className="sparkle s3" />
    </svg>
  );

  const Row = ({ label, v1, v2, isLowerBetter = false, formatFn = null }) => {
    let v1Better = false;
    let v2Better = false;

    const val1 = v1 === NA ? NA : (formatFn ? formatFn(v1) : val(v1));
    const val2 = v2 === NA ? NA : (formatFn ? formatFn(v2) : val(v2));

    if (val1 === NA && val2 === NA) {
      return null;
    }

    if (v1 !== null && v2 !== null && v1 !== undefined && v2 !== undefined && v1 !== "" && v2 !== "" && v1 !== NA && v2 !== NA) {
      const num1 = parseFloat(v1);
      const num2 = parseFloat(v2);

      if (!isNaN(num1) && !isNaN(num2) && num1 !== num2) {
        if (isLowerBetter) {
          v1Better = num1 < num2;
          v2Better = num2 < num1;
        } else {
          v1Better = num1 > num2;
          v2Better = num2 > num1;
        }
      }
    }

    return (
      <tr>
        <td className="spec-label">{label}</td>
        <td className={val1 === NA ? "na-cell" : (v1Better ? "better-spec" : "")}>
          <div className="spec-value-container">
            <span>{val1}</span>
            {v1Better && <BetterTick />}
          </div>
        </td>
        <td className={val2 === NA ? "na-cell" : (v2Better ? "better-spec" : "")}>
          <div className="spec-value-container">
            <span>{val2}</span>
            {v2Better && <BetterTick />}
          </div>
        </td>
      </tr>
    );
  };

  const SectionHeader = ({ label }) => (
    <tr className="section-header">
      <td colSpan={3}>{label}</td>
    </tr>
  );

  return (
    <div className="comparison-page">
      <div className="container">
        <h1>So Sánh Phiên Bản Xe</h1>
        <p className="subtitle">Chọn 2 phiên bản xe bất kỳ để so sánh toàn bộ thông số kỹ thuật</p>

        {/* Selection Section */}
        <div className="selection-section">
          <div className="select-group">
            <label>Phiên Bản Xe Thứ 1</label>
            <select value={variant1Id} onChange={(e) => setVariant1Id(e.target.value)}>
              <option value="">-- Chọn phiên bản xe --</option>
              {variants.map((v) => (
                <option key={v.variant_id} value={v.variant_id}>
                  {v.vehicle_name} — {v.variant_name}
                </option>
              ))}
            </select>
          </div>

          <div className="vs-badge">VS</div>

          <div className="select-group">
            <label>Phiên Bản Xe Thứ 2</label>
            <select value={variant2Id} onChange={(e) => setVariant2Id(e.target.value)}>
              <option value="">-- Chọn phiên bản xe --</option>
              {variants.map((v) => (
                <option key={v.variant_id} value={v.variant_id}>
                  {v.vehicle_name} — {v.variant_name}
                </option>
              ))}
            </select>
          </div>

          <button className="btn btn-primary compare-btn" onClick={handleCompare} disabled={loading}>
            {loading ? "Đang tải..." : "🔍 So Sánh"}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Comparison Table */}
        {vehicle1 && vehicle2 && (
          <div className="comparison-result">
            <div className="comparison-header-row">
              <div className="comparison-header-cell label-cell"></div>
              <div className="comparison-header-cell">
                {vehicle1.thumbnail && <img src={vehicle1.thumbnail} alt={vehicle1.vehicle_name} className="comparison-vehicle-img" />}
                <h3>{vehicle1.vehicle_name}</h3>
                <p>{vehicle1.variant_name}</p>
              </div>
              <div className="comparison-header-cell">
                {vehicle2.thumbnail && <img src={vehicle2.thumbnail} alt={vehicle2.vehicle_name} className="comparison-vehicle-img" />}
                <h3>{vehicle2.vehicle_name}</h3>
                <p>{vehicle2.variant_name}</p>
              </div>
            </div>

            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <tbody>
                  {/* ====== THÔNG TIN CHUNG ====== */}
                  <SectionHeader label="📋 Thông Tin Chung" />
                  <Row label="Loại Xe" v1={vehicle1.vehicle_type} v2={vehicle2.vehicle_type} />
                  <Row label="Phân Khúc" v1={vehicle1.segment} v2={vehicle2.segment} />
                  <Row label="Số Chỗ Ngồi" v1={vehicle1.seating_capacity} v2={vehicle2.seating_capacity} />
                  <Row
                    label="Giá Niêm Yết"
                    v1={vehicle1.price?.published_price}
                    v2={vehicle2.price?.published_price}
                    isLowerBetter={true}
                    formatFn={formatPrice}
                  />
                  <Row
                    label="Giá Lăn Bánh"
                    v1={vehicle1.price?.final_price}
                    v2={vehicle2.price?.final_price}
                    isLowerBetter={true}
                    formatFn={formatPrice}
                  />

                  {/* ====== ĐỘNG CƠ & HIỆU SUẤT ====== */}
                  <SectionHeader label="⚡ Động Cơ & Hiệu Suất" />
                  <Row label="Loại Động Cơ" v1={vehicle1.engine?.engine_type} v2={vehicle2.engine?.engine_type} />
                  <Row label="Dẫn Động" v1={vehicle1.engine?.drive_type} v2={vehicle2.engine?.drive_type} />
                  <Row label="Số Motor" v1={vehicle1.engine?.motor_count} v2={vehicle2.engine?.motor_count} />
                  <Row label="Công Suất Tối Đa (kW)" v1={vehicle1.engine?.max_power_kw} v2={vehicle2.engine?.max_power_kw} />
                  <Row label="Mô-men Xoắn Tối Đa (Nm)" v1={vehicle1.engine?.max_torque_nm} v2={vehicle2.engine?.max_torque_nm} />
                  <Row label="Tốc Độ Tối Đa (km/h)" v1={vehicle1.engine?.max_speed_kmh} v2={vehicle2.engine?.max_speed_kmh} />
                  <Row label="0-100 km/h (giây)" v1={vehicle1.engine?.acceleration_0_100_seconds} v2={vehicle2.engine?.acceleration_0_100_seconds} isLowerBetter={true} />

                  {/* ====== PIN & SẠC ====== */}
                  <SectionHeader label="🔋 Pin & Sạc" />
                  <Row label="Loại Pin" v1={vehicle1.battery?.battery_type} v2={vehicle2.battery?.battery_type} />
                  <Row label="Dung Lượng Pin (kWh)" v1={vehicle1.battery?.battery_capacity_kwh} v2={vehicle2.battery?.battery_capacity_kwh} />
                  <Row label="Phạm Vi WLTP (km)" v1={vehicle1.battery?.wltp_range_km} v2={vehicle2.battery?.wltp_range_km} />
                  <Row label="Phạm Vi NEDC (km)" v1={vehicle1.battery?.nedc_range_km} v2={vehicle2.battery?.nedc_range_km} />
                  <Row label="Sạc DC Nhanh (kW)" v1={vehicle1.battery?.dc_fast_charge_kw} v2={vehicle2.battery?.dc_fast_charge_kw} />
                  <Row label="Thời Gian Sạc Nhanh (phút)" v1={vehicle1.battery?.fast_charge_time_minutes} v2={vehicle2.battery?.fast_charge_time_minutes} isLowerBetter={true} />
                  <Row label="Sạc AC 1P (kW)" v1={vehicle1.battery?.ac_slow_1phase_kw} v2={vehicle2.battery?.ac_slow_1phase_kw} />
                  <Row label="Sạc AC 3P (kW)" v1={vehicle1.battery?.ac_slow_3phase_kw} v2={vehicle2.battery?.ac_slow_3phase_kw} />

                  {/* ====== HỘP SỐ & LÁI ====== */}
                  <SectionHeader label="⚙️ Hộp Số & Lái" />
                  <Row label="Loại Hộp Số" v1={vehicle1.transmission?.transmission_type} v2={vehicle2.transmission?.transmission_type} />
                  <Row label="Chế Độ Lái" v1={vehicle1.transmission?.drive_mode_selection} v2={vehicle2.transmission?.drive_mode_selection} />
                  <Row label="Nút Chuyển Số" v1={vehicle1.transmission?.gear_shifting_buttons} v2={vehicle2.transmission?.gear_shifting_buttons} />
                  <Row label="Tự Động Dừng" v1={vehicle1.transmission?.idle_stop_feature} v2={vehicle2.transmission?.idle_stop_feature} />

                  {/* ====== KÍCH THƯỚC & KHUNG GẦM ====== */}
                  <SectionHeader label="📐 Kích Thước & Khung Gầm" />
                  <Row label="Chiều Dài (mm)" v1={vehicle1.dimension?.length_mm} v2={vehicle2.dimension?.length_mm} />
                  <Row label="Chiều Rộng (mm)" v1={vehicle1.dimension?.width_mm} v2={vehicle2.dimension?.width_mm} />
                  <Row label="Chiều Cao (mm)" v1={vehicle1.dimension?.height_mm} v2={vehicle2.dimension?.height_mm} />
                  <Row label="Chiều Dài Cơ Sở (mm)" v1={vehicle1.dimension?.wheelbase_mm} v2={vehicle2.dimension?.wheelbase_mm} />
                  <Row label="Khoảng Sáng Gầm (mm)" v1={vehicle1.dimension?.ground_clearance_mm} v2={vehicle2.dimension?.ground_clearance_mm} />
                  <Row label="Trọng Lượng (kg)" v1={vehicle1.dimension?.curb_weight_kg} v2={vehicle2.dimension?.curb_weight_kg} isLowerBetter={true} />
                  <Row label="Thể Tích Khoang Hành Lý (L)" v1={vehicle1.dimension?.cargo_capacity_with_seats_l} v2={vehicle2.dimension?.cargo_capacity_with_seats_l} />
                  <Row label="Hệ Thống Treo Trước" v1={vehicle1.suspension?.front_suspension} v2={vehicle2.suspension?.front_suspension} />
                  <Row label="Hệ Thống Treo Sau" v1={vehicle1.suspension?.rear_suspension} v2={vehicle2.suspension?.rear_suspension} />
                  <Row label="Phanh Trước" v1={vehicle1.suspension?.front_brakes} v2={vehicle2.suspension?.front_brakes} />
                  <Row label="Phanh Sau" v1={vehicle1.suspension?.rear_brakes} v2={vehicle2.suspension?.rear_brakes} />
                  <Row label="Kích Thước Mâm (inch)" v1={vehicle1.suspension?.rim_size_inches} v2={vehicle2.suspension?.rim_size_inches} />

                  {/* ====== NGOẠI THẤT ====== */}
                  <SectionHeader label="✨ Ngoại Thất" />
                  <Row label="Đèn Pha" v1={vehicle1.exterior?.headlight_type} v2={vehicle2.exterior?.headlight_type} />
                  <Row label="Đèn Chạy Ban Ngày (DRL)" v1={vehicle1.exterior?.daytime_running_lights} v2={vehicle2.exterior?.daytime_running_lights} />
                  <Row label="Đèn Sương Mù Trước" v1={vehicle1.exterior?.fog_lights_front} v2={vehicle2.exterior?.fog_lights_front} />
                  <Row label="Đèn Hậu" v1={vehicle1.exterior?.rear_lights} v2={vehicle2.exterior?.rear_lights} />
                  <Row label="Cửa Sổ Trời Panorama" v1={vehicle1.exterior?.panoramic_sunroof} v2={vehicle2.exterior?.panoramic_sunroof} />

                  {/* ====== NỘI THẤT ====== */}
                  <SectionHeader label="🛋️ Nội Thất" />
                  <Row label="Chất Liệu Ghế" v1={vehicle1.interior?.seat_material} v2={vehicle2.interior?.seat_material} />
                  <Row label="Điều Chỉnh Ghế Lái" v1={vehicle1.interior?.driver_seat_adjustment} v2={vehicle2.interior?.driver_seat_adjustment} />
                  <Row label="Ghế Lái Nhớ Vị Trí" v1={vehicle1.interior?.driver_seat_memory} v2={vehicle2.interior?.driver_seat_memory} />
                  <Row label="Sưởi Ghế Lái" v1={vehicle1.interior?.driver_seat_heating} v2={vehicle2.interior?.driver_seat_heating} />
                  <Row label="Thông Gió Ghế Lái" v1={vehicle1.interior?.driver_seat_ventilation} v2={vehicle2.interior?.driver_seat_ventilation} />
                  <Row label="Massage Ghế Lái" v1={vehicle1.interior?.driver_seat_massage} v2={vehicle2.interior?.driver_seat_massage} />
                  <Row label="Điều Hòa" v1={vehicle1.interior?.air_conditioning_type} v2={vehicle2.interior?.air_conditioning_type} />
                  <Row label="Vô Lăng" v1={vehicle1.interior?.steering_wheel_type} v2={vehicle2.interior?.steering_wheel_type} />
                  <Row label="Sạc Không Dây" v1={vehicle1.interior?.wireless_charging} v2={vehicle2.interior?.wireless_charging} />

                  {/* ====== GIẢI TRÍ & KẾT NỐI ====== */}
                  <SectionHeader label="🎵 Giải Trí & Kết Nối" />
                  <Row label="Màn Hình Trung Tâm (inch)" v1={vehicle1.entertainment?.infotainment_screen_inches} v2={vehicle2.entertainment?.infotainment_screen_inches} />
                  <Row label="Màn Hình Hàng Sau (inch)" v1={vehicle1.entertainment?.rear_screen_inches} v2={vehicle2.entertainment?.rear_screen_inches} />
                  <Row label="HUD" v1={vehicle1.entertainment?.hud_display} v2={vehicle2.entertainment?.hud_display} />
                  <Row label="Số Loa" v1={vehicle1.entertainment?.speaker_count} v2={vehicle2.entertainment?.speaker_count} />
                  <Row label="Bluetooth" v1={vehicle1.entertainment?.bluetooth_support} v2={vehicle2.entertainment?.bluetooth_support} />
                  <Row label="Android Auto" v1={vehicle1.entertainment?.android_auto_support} v2={vehicle2.entertainment?.android_auto_support} />
                  <Row label="Apple CarPlay" v1={vehicle1.entertainment?.apple_carplay_support} v2={vehicle2.entertainment?.apple_carplay_support} />

                  {/* ====== AN TOÀN ====== */}
                  <SectionHeader label="🛡️ An Toàn" />
                  <Row label="Túi Khí Trước" v1={vehicle1.safety?.front_airbags} v2={vehicle2.safety?.front_airbags} />
                  <Row label="Túi Khí Rèm" v1={vehicle1.safety?.curtain_airbags} v2={vehicle2.safety?.curtain_airbags} />
                  <Row label="Túi Khí Hông Trước" v1={vehicle1.safety?.side_airbags_front} v2={vehicle2.safety?.side_airbags_front} />
                  <Row label="ABS" v1={vehicle1.safety?.abs_system} v2={vehicle2.safety?.abs_system} />
                  <Row label="EBD" v1={vehicle1.safety?.ebd_system} v2={vehicle2.safety?.ebd_system} />
                  <Row label="ESC" v1={vehicle1.safety?.esc_system} v2={vehicle2.safety?.esc_system} />
                  <Row label="Cảm Biến Áp Suất Lốp (TPMS)" v1={vehicle1.safety?.tire_pressure_monitoring} v2={vehicle2.safety?.tire_pressure_monitoring} />
                  <Row label="Điểm Cài ISOFIX" v1={vehicle1.safety?.isofix_child_seat_mount} v2={vehicle2.safety?.isofix_child_seat_mount} />

                  {/* ====== HỖ TRỢ LÁI (ADAS) ====== */}
                  <SectionHeader label="🤖 Hỗ Trợ Lái (ADAS)" />
                  <Row label="Camera Lùi" v1={vehicle1.adas?.rear_view_camera} v2={vehicle2.adas?.rear_view_camera} />
                  <Row label="Camera 360°" v1={vehicle1.adas?.surround_view_camera} v2={vehicle2.adas?.surround_view_camera} />
                  <Row label="Cảnh Báo Va Chạm" v1={vehicle1.adas?.forward_collision_warning} v2={vehicle2.adas?.forward_collision_warning} />
                  <Row label="Phanh Khẩn Cấp Tự Động (AEB)" v1={vehicle1.adas?.autonomous_emergency_braking} v2={vehicle2.adas?.autonomous_emergency_braking} />
                  <Row label="Giữ Làn Đường (LKA)" v1={vehicle1.adas?.lane_keep_assist} v2={vehicle2.adas?.lane_keep_assist} />
                  <Row label="Cruise Control Thích Ứng (ACC)" v1={vehicle1.adas?.adaptive_cruise_control} v2={vehicle2.adas?.adaptive_cruise_control} />
                  <Row label="Hỗ Trợ Cao Tốc (HDA)" v1={vehicle1.adas?.highway_driving_assist} v2={vehicle2.adas?.highway_driving_assist} />
                  <Row label="Cảnh Báo Điểm Mù (BSD)" v1={vehicle1.adas?.blind_spot_detection} v2={vehicle2.adas?.blind_spot_detection} />
                  <Row label="Hỗ Trợ Đỗ Xe (PA)" v1={vehicle1.adas?.parking_assist} v2={vehicle2.adas?.parking_assist} />
                </tbody>
              </table>
            </div>

            {/* CTA */}
            <div className="comparison-actions">
              <a href="/booking" className="btn btn-primary">Liên Hệ Với Sale</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonPage;
